// tokenService.js - REPLACE THE ENTIRE FILE
import firebase, { auth } from '../firebaseConfig.js';

// In-memory token storage (not persisted to localStorage for security)
let authToken = null;
let jwtToken = null;
let csrfToken = null;
let tokenExpiry = null;

/**
 * Get a valid Firebase auth token
 * @returns {Promise<string>} Firebase auth token
 */
export async function getAuthToken() {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No user signed in');
  }
  
  // If we already have a token, use it
  if (authToken) {
    return authToken;
  }
  
  // Get a new token
  try {
    authToken = await user.getIdToken(true);
    return authToken;
  } catch (error) {
    console.error('Error getting auth token:', error);
    throw error;
  }
}

/**
 * Get a JWT token from the server
 * @returns {Promise<string>} JWT token
 */
export async function getJWTToken() {
  try {
    const now = Date.now();
    
    // If we already have a valid token that's not expired, use it
    if (jwtToken && tokenExpiry && now < tokenExpiry) {
      return jwtToken;
    }
    
    // Try to get token from localStorage
    const storedToken = localStorage.getItem('jwtToken');
    const storedExpiry = localStorage.getItem('jwtTokenExpiry');
    
    if (storedToken && storedExpiry && now < parseInt(storedExpiry)) {
      // Use stored token
      jwtToken = storedToken;
      tokenExpiry = parseInt(storedExpiry);
      return jwtToken;
    }
    
    // Get Firebase token first
    const firebaseToken = await getAuthToken();
    
    // Exchange for JWT
    const response = await fetch('/api/auth/create-token', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${firebaseToken}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to get JWT token');
    }
    
    const data = await response.json();
    jwtToken = data.token;
    
    // Set token expiry (default to 55 minutes if not provided by server)
    // This gives a 5-minute buffer before the actual 1-hour expiry
    tokenExpiry = now + (data.expiresIn || (55 * 60 * 1000));
    
    // Store token in localStorage for persistence
    localStorage.setItem('jwtToken', jwtToken);
    localStorage.setItem('jwtTokenExpiry', tokenExpiry.toString());
    
    return jwtToken;
  } catch (error) {
    console.error('Error getting JWT token:', error);
    throw error;
  }
}

/**
 * Get CSRF token from server
 * @returns {Promise<string>} CSRF token
 */
export async function getCSRFToken() {
  if (csrfToken) return csrfToken;
  
  const response = await fetch('/api/auth/csrf-token', {
    credentials: 'include' // Important for cookies
  });
  
  if (!response.ok) {
    throw new Error('Failed to get CSRF token');
  }
  
  const data = await response.json();
  csrfToken = data.csrfToken;
  
  return csrfToken;
}

/**
 * Clear all tokens
 */
export function clearTokens() {
  authToken = null;
  jwtToken = null;
  csrfToken = null;
  tokenExpiry = null;
  
  // Clear token-related localStorage items
  localStorage.removeItem('jwtToken');
  localStorage.removeItem('jwtTokenExpiry');
  localStorage.removeItem('csrfToken');
}

export default {
  getAuthToken,
  getJWTToken,
  getCSRFToken,
  clearTokens
};